import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { OutlineLinkButton, PrimaryButton } from '../buttons';
import { InfoCircle } from '../svg/infoCircle';
import { CheckSolid } from '../svg/checkSolid';
import PointRightFinger from '../svg/pointRightFinger';
import CloseIcon from '../icons/times-solid.svg';
import Crown from '../svg/crown';
import Bullhorn from '../svg/bullhorn';

export type ThemeNotificationBanner = 'CRITICAL' | 'WARNING' | 'SUCCESS' | 'INFO' | 'EXTRA' | 'PROMO';
export type ButtonsPosition = 'FOOTER' | 'INLINE';
export type BannerVariant = 'slim' | 'extended';
type ButtonSizeType = 'small' | 'medium' | 'large';

export interface BannerButton {
    label: string;
    outline?: boolean;
    linkButton?: boolean;
    disabled?: boolean;
    dataTrack?: string;
    onClick?: () => void;
    isSubmit?: boolean;
    size?: ButtonSizeType;
}

const NotificationBannerWrap = styled.div<{
    $buttonsPosition?: ButtonsPosition;
    $themeComponent: 'danger' | 'warning' | 'information' | 'success' | 'extra' | 'promo';
    $variant?: BannerVariant;
}>`
    display: grid;
    width: 100%;
    grid-template-columns: ${(props) => (props.$variant === 'slim' ? '40px 1fr' : props.$buttonsPosition === 'FOOTER' ? '32px 1fr' : '43px 1fr')};
    box-sizing: border-box;
    background: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].backgroundColor};
    border-radius: 8px;

    > * {
        box-sizing: border-box;
    }

    *,
    *:before,
    *:after {
        box-sizing: border-box;
    }
`;

const NotificationBannerIcon = styled.div<{
    $buttonsPosition?: ButtonsPosition;
    $themeComponent: 'danger' | 'warning' | 'information' | 'success' | 'extra' | 'promo';
    $variant?: BannerVariant;
}>`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px 0px 0px 8px;
    border-top: 1px solid ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    border-bottom: 1px solid ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    border-left: 1px solid ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    background: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};

    svg {
        width: ${(props) => (props.$buttonsPosition === 'FOOTER' ? '23px' : '26px')};
    }
`;

const NotificationBannerTitle = styled.span<{
    $variant?: BannerVariant;
}>`
    color: #000000;
    font-size: ${(props) => (props.$variant === 'slim' ? '14px' : '15px')};
    font-style: normal;
    font-weight: 700;
    line-height: ${(props) => (props.$variant === 'slim' ? 'normal' : '22px')};
    white-space: ${(props) => (props.$variant === 'slim' ? 'nowrap' : 'normal')};
`;

const NotificationBannerContentWrap = styled.div<{
    $variant?: BannerVariant;
}>`
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
    font-size: 14px;
    align-items: center;
    gap: ${(props) => (props.$variant === 'slim' ? '16px' : '25px')};
    flex: 1;
`;

const InnerContainer = styled.div<{
    $variant?: BannerVariant;
}>`
    width: 100%;
    display: flex;
    flex-direction: ${(props) => (props.$variant === 'slim' ? 'row' : 'column')};
    justify-content: ${(props) => (props.$variant === 'slim' ? 'flex-start' : 'left')};
    align-items: ${(props) => (props.$variant === 'slim' ? 'center' : 'flex-start')};
    gap: 5px;
`;

const NotificationBannerContainer = styled.div<{
    $hasButton: boolean;
    $buttonsPosition?: ButtonsPosition;
    $variant?: BannerVariant;
}>`
    width: 100%;
    display: flex;
    align-items: ${(props) => (props.$buttonsPosition === 'FOOTER' ? 'normal' : 'center ')};
    flex-direction: ${(props) => (props.$buttonsPosition === 'FOOTER' ? 'column' : 'row ')};
    gap: ${(props) => (props.$buttonsPosition === 'FOOTER' ? '10px' : '28px')};
    ${(props) =>
        props.$hasButton &&
        `
        justify-content: space-between;
    `}
`;

const NotificationBannerContent = styled.div`
    color: #000000;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-align: left;
    white-space: pre-line;
`;

const NotificationBannerButton = styled(PrimaryButton)<{
    $themeComponent: 'danger' | 'warning' | 'information' | 'success' | 'extra' | 'promo';
    $variant?: BannerVariant;
}>`
    min-width: ${(props) => (props.$variant === 'slim' ? 'auto' : '200px')};
    white-space: nowrap;
    background-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    border-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};

    &[disabled] {
        border-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
        background-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor} !important;
        opacity: 0.9;
    }
`;

const NotificationBannerOutlineButton = styled(OutlineLinkButton)<{
    $themeComponent: 'danger' | 'warning' | 'information' | 'success' | 'extra' | 'promo';
    $variant?: BannerVariant;
}>`
    min-width: ${(props) => (props.$variant === 'slim' ? 'auto' : '200px')};
    white-space: nowrap;
    border: ${(props) => props.theme.app.component.button.basic.border} !important;
    color: #202223 !important;
    ${(props) => props.$variant === 'slim' && `padding-top: 7px; padding-bottom: 7px;`}

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor} !important;
        color: ${(props) => props.theme.app.component.button.basic.hover.textColor} !important;
    }
`;

const NotificationBannerLinkButton = styled(PrimaryButton)<{
    $themeComponent: 'danger' | 'warning' | 'information' | 'success' | 'extra' | 'promo';
    $variant?: BannerVariant;
}>`
    background: none;
    text-align: center;
    white-space: nowrap;
    border: none;
    padding: 0;
    color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};

    &:disabled {
        background: none;
        border: none;
        color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
        opacity: 0.5;
    }

    &:hover:not([disabled]) {
        background: none;
        color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
        opacity: 0.9;
    }

    &:hover {
        background: none;
        color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor} !important;
    }

    &:active:not([disabled]) {
        background: none;
        color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    }

    &:active {
        background: none;
    }
`;

const NotificationBannerButtons = styled.div<{
    $variant?: BannerVariant;
}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${(props) => (props.$variant === 'slim' ? '12px' : '28px')};
    justify-content: flex-end;
    div:last-child {
        margin-right: 0;
    }
`;

const NotificationBannerCloseWrap = styled.div`
    position: relative;
    display: flex;
    padding-left: 8px;
    height: 100%;
    align-self: flex-start;
    border-left: 1px solid rgba(0, 0, 0, 0.1);
`;

const NotificationBannerClose = styled(CloseIcon)`
    cursor: pointer;
    width: 16px;
    height: 16px;
    color: rgba(0, 0, 0, 0.1);
    opacity: 0.5;

    &:hover {
        opacity: 0.5;
    }
`;

interface NotificationBannerProps {
    title?: ReactNode;
    content: ReactNode;
    type?: ThemeNotificationBanner;
    showCloseButton?: boolean;
    className?: string;
    onClose?: () => void;
    customIcon?: ReactNode;
    buttons?: BannerButton[];
    buttonsPosition?: ButtonsPosition;
    variant?: BannerVariant;
    slimButtonSize?: ButtonSizeType;
}

const _NotificationBanner: FC<NotificationBannerProps> = ({
    title,
    content,
    type,
    showCloseButton,
    className,
    onClose,
    customIcon,
    buttons,
    buttonsPosition,
    variant = 'extended',
    slimButtonSize = 'small'
}) => {
    const icon =
        type === 'CRITICAL' ? (
            <InfoCircle></InfoCircle>
        ) : type === 'WARNING' ? (
            <InfoCircle></InfoCircle>
        ) : type === 'SUCCESS' ? (
            <CheckSolid></CheckSolid>
        ) : type === 'INFO' ? (
            <PointRightFinger></PointRightFinger>
        ) : type === 'EXTRA' ? (
            <Crown></Crown>
        ) : type === 'PROMO' ? (
            <Bullhorn></Bullhorn>
        ) : (
            <PointRightFinger></PointRightFinger>
        );

    const themeComponent =
        type === 'CRITICAL'
            ? 'danger'
            : type === 'WARNING'
              ? 'warning'
              : type === 'SUCCESS'
                ? 'success'
                : type === 'INFO'
                  ? 'information'
                  : type === 'EXTRA'
                    ? 'extra'
                    : type === 'PROMO'
                      ? 'promo'
                      : 'information';

    const getButtonSize = (button: BannerButton): ButtonSizeType => {
        if (button.size) return button.size;
        return variant === 'slim' ? slimButtonSize : 'medium';
    };

    return (
        <NotificationBannerWrap className={className} $buttonsPosition={buttonsPosition} $themeComponent={themeComponent} $variant={variant}>
            <NotificationBannerIcon $buttonsPosition={buttonsPosition} $themeComponent={themeComponent} $variant={variant}>
                {customIcon ?? icon}
            </NotificationBannerIcon>
            <NotificationBannerContentWrap $variant={variant}>
                <InnerContainer $variant={variant}>
                    {title && <NotificationBannerTitle $variant={variant}>{title}</NotificationBannerTitle>}
                    <NotificationBannerContainer $hasButton={(buttons?.length ?? 0) > 0} $buttonsPosition={buttonsPosition} $variant={variant}>
                        <NotificationBannerContent>{content}</NotificationBannerContent>
                        {buttons && buttons.length > 0 && (
                            <NotificationBannerButtons $variant={variant}>
                                {buttons.map((button, index) => {
                                    return button.linkButton ? (
                                        <NotificationBannerLinkButton
                                            type={button.isSubmit ? 'submit' : 'button'}
                                            $themeComponent={themeComponent}
                                            $variant={variant}
                                            size={getButtonSize(button)}
                                            data-track={button.dataTrack}
                                            disabled={button.disabled ?? false}
                                            key={`Button_${index}`}
                                            onClick={button.onClick}
                                        >
                                            {button.label}
                                        </NotificationBannerLinkButton>
                                    ) : button.outline ? (
                                        <NotificationBannerOutlineButton
                                            type={button.isSubmit ? 'submit' : 'button'}
                                            $themeComponent={themeComponent}
                                            $variant={variant}
                                            data-track={button.dataTrack}
                                            disabled={button.disabled ?? false}
                                            key={`Button_${index}`}
                                            onClick={button.onClick}
                                        >
                                            {button.label}
                                        </NotificationBannerOutlineButton>
                                    ) : (
                                        <NotificationBannerButton
                                            type={button.isSubmit ? 'submit' : 'button'}
                                            $themeComponent={themeComponent}
                                            $variant={variant}
                                            size={getButtonSize(button)}
                                            data-track={button.dataTrack}
                                            disabled={button.disabled ?? false}
                                            key={`Button_${index}`}
                                            onClick={button.onClick}
                                        >
                                            {button.label}
                                        </NotificationBannerButton>
                                    );
                                })}
                            </NotificationBannerButtons>
                        )}
                    </NotificationBannerContainer>
                </InnerContainer>
                {showCloseButton && (
                    <NotificationBannerCloseWrap>
                        <NotificationBannerClose onClick={onClose} />
                    </NotificationBannerCloseWrap>
                )}
            </NotificationBannerContentWrap>
        </NotificationBannerWrap>
    );
};

export const NotificationBanner = styled(_NotificationBanner)``;

export default NotificationBanner;
